import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Calendar, AlertCircle, Clock } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import CalendarEventForm from './CalendarEventForm';

export const CalendarEvents = () => {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { user } = useAuth();
  const [calendarevents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCalendarEvents = async () => {
    try {
      setLoading(true);
      setError(null);
  
      const response = await get('/calendar-events');
  
      if (response.data && Array.isArray(response.data)) {
        setCalendarEvents(response.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Error fetching CalendarEvents:', err);
      setError(err.message || 'Failed to load CalendarEvents');
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    fetchCalendarEvents();
  }, []);

  const canBookCalendarEvents = ['admin', 'receptionist'].includes(user?.role);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      );
    }

    if (!calendarevents.length) {
      return (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No Calendar Events found</h3>
          <p className="mt-1 text-gray-500">
            {canBookCalendarEvents ? 'Click "Add Calendar Event" to get started' : 'No Calendar Events available at this time'}
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {calendarevents.map((event) => (
          <div 
            key={event._id} 
            className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-semibold text-gray-900">{event.name}</h2>
              <span className="px-3 py-1 rounded-full text-sm font-medium capitalize bg-blue-100 text-blue-800">
                {event.type}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center text-gray-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>{formatDate(event.date)}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Clock className="h-4 w-4 mr-2" />
                <span>{event.startTime} - {event.endTime}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Calendar Events</h1>
        {canBookCalendarEvents && (
          <button
            onClick={() => setShowBooking(true)}
            className="flex items-center px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 transition-colors duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Calendar Event
          </button>
        )}
      </div>

      {renderContent()}

      {showBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <CalendarEventForm
              onClose={() => {
                setShowBooking(false);
                fetchCalendarEvents();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarEvents;